import * as yup from 'yup';
import { VALIDATION_RULES } from './constants';

export const contactFormSchema = yup.object().shape({
    firstName: yup
        .string()
        .required('First name is required')
        .min(VALIDATION_RULES.MIN_NAME_LENGTH, `First name must be at least ${VALIDATION_RULES.MIN_NAME_LENGTH} characters`)
        .max(VALIDATION_RULES.MAX_NAME_LENGTH, `First name cannot exceed ${VALIDATION_RULES.MAX_NAME_LENGTH} characters`),

    lastName: yup
        .string()
        .required('Last name is required')
        .min(VALIDATION_RULES.MIN_NAME_LENGTH, `Last name must be at least ${VALIDATION_RULES.MIN_NAME_LENGTH} characters`)
        .max(VALIDATION_RULES.MAX_NAME_LENGTH, `Last name cannot exceed ${VALIDATION_RULES.MAX_NAME_LENGTH} characters`),

    email: yup
        .string()
        .required('Email is required')
        .matches(VALIDATION_RULES.EMAIL_REGEX, 'Please enter a valid email address'),

    phone: yup
        .string()
        .required('Phone number is required')
        .matches(VALIDATION_RULES.PHONE_REGEX, 'Please enter a valid Australian phone number'),

    address: yup
        .string()
        .required('Address is required')
        .min(VALIDATION_RULES.MIN_ADDRESS_LENGTH, `Address must be at least ${VALIDATION_RULES.MIN_ADDRESS_LENGTH} characters`)
        .max(VALIDATION_RULES.MAX_ADDRESS_LENGTH, `Address cannot exceed ${VALIDATION_RULES.MAX_ADDRESS_LENGTH} characters`),

    bookingOption: yup
        .string()
        .required('Please select a booking option')
});

export const measurementFormSchema = yup.object().shape({
    glassCount: yup
        .number()
        .required('Number of glass panes is required')
        .min(1, 'Must have at least 1 pane')
        .max(50, 'Cannot exceed 50 panes'),

    painPoint: yup
        .string()
        .required('Please select your main concern'),

    jobDescription: yup
        .string()
        .default('')
        .max(5000, 'Job description cannot exceed 5000 characters'),

    measurements: yup
        .array()
        .of(
            yup.object().shape({
                length: yup.number().required('Length is required').min(0, 'Length must be positive'),
                width: yup.number().required('Width is required').min(0, 'Width must be positive'),
                sqm: yup.number().required('Square meters is required').min(0, 'Square meters must be positive'),
            }).required()
        )
        .required('Measurements are required')
        .min(1, 'At least one measurement is required')
});

export const bookingFormSchema = yup.object().shape({
    selectedDate: yup
        .string()
        .required('Please select a date'),

    selectedTimeSlot: yup
        .object()
        .required('Please select a time slot'),

    bookingType: yup
        .string()
        .required('Please select a booking option')
});

export const validateForm = <T>(schema: yup.Schema<T>, data: T): { isValid: boolean; errors: Record<string, string> } => {
    try {
        schema.validateSync(data, { abortEarly: false });
        return { isValid: true, errors: {} };
    } catch (error) {
        if (error instanceof yup.ValidationError) {
            const errors: Record<string, string> = {};
            error.inner.forEach((err) => {
                if (err.path) {
                    errors[err.path] = err.message;
                }
            });
            return { isValid: false, errors };
        }
        return { isValid: false, errors: { general: 'Validation failed' } };
    }
};

export const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');

    // Format Australian phone numbers
    if (digits.startsWith('61')) {
        // International format
        return `+${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
    } else if (digits.startsWith('0')) {
        // National format
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }

    return phone;
};

export const sanitizeInput = (input: string): string => {
    return input.trim().replace(/[<>]/g, '');
};
